/* ============================================
   Nueva / Editar Cotización — Equipo Dedicado
   Manages team composition, internal dashboard,
   margin alerts, and commercial price modifiers
   ============================================ */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { showToast } from '@/components/layout/Toast'
import type { Cliente, ContactoVinculado, Empresa, PerfilEquipo, EquipoMiembro, EquipoTotales, CotizacionItemsEquipo, EstadoCotizacion, Moneda, Cotizacion, Seccion } from '@/lib/types'
import { contactoFacturacionDe } from '@/lib/types'
import SelectorContactoEmpresa, { nombreContacto } from '@/components/contactos/SelectorContactoEmpresa'
import { nextCotNumber, r2, fmtFTE, marginClass } from '@/lib/utils'
import { PageHeader } from '@/components/ui'
const MARGEN_MINIMO = 30

type Dedicacion = '1.0' | '0.5' | '0.25' | 'custom'

export default function NuevaCotEquipo() {
  const db = useSupabase()
  const nav = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id

  // Reference data
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [perfiles, setPerfiles] = useState<PerfilEquipo[]>([])
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Form state
  const [numero, setNumero] = useState('')
  const [clienteId, setClienteId] = useState<number | null>(null)
  const [contacto, setContacto] = useState('')
  const [telefono, setTelefono] = useState('')
  const [ruc, setRuc] = useState('')
  const [email, setEmail] = useState('')
  const [clienteContactos, setClienteContactos] = useState<ContactoVinculado[]>([])
  const [contactoSelId, setContactoSelId] = useState<number | null>(null)
  const [proyecto, setProyecto] = useState('')
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0])
  const [validez, setValidez] = useState(90)
  const [moneda, setMoneda] = useState<Moneda>('USD')
  const [estado, setEstado] = useState<EstadoCotizacion>('borrador')
  const [igvPct, setIgvPct] = useState(18)
  const [descPct, setDescPct] = useState(0)
  const [notas, setNotas] = useState('')
  const [secciones, setSecciones] = useState<Seccion[]>([])

  // Team
  const [team, setTeam] = useState<EquipoMiembro[]>([])
  const [precioFinalOverride, setPrecioFinalOverride] = useState<number | null>(null)

  // Add member form
  const [addPerfilId, setAddPerfilId] = useState<number | ''>('')
  const [addCant, setAddCant] = useState(1)
  const [addDed, setAddDed] = useState<Dedicacion>('1.0')
  const [addCustomHours, setAddCustomHours] = useState(0)

  // Calculations
  const totalHoras = team.reduce((s, m) => s + m.horas_mensuales_linea, 0)
  const costoTotal = r2(team.reduce((s, m) => s + m.costo_total_linea, 0))
  const precioSugerido = r2(team.reduce((s, m) => s + m.precio_venta_linea, 0))
  const precioFinal = precioFinalOverride ?? precioSugerido
  const margenGlobal = precioFinal > 0 ? r2(((precioFinal - costoTotal) / precioFinal) * 100) : 0
  const igvMonto = r2(precioFinal * igvPct / 100)
  const descMonto = r2(precioFinal * descPct / 100)
  const total = r2(precioFinal + igvMonto - descMonto)

  useEffect(() => {
    (async () => {
      try {
        const [c, p, e, allCot] = await Promise.all([
          db.getAll<Cliente>('clientes'), db.getAll<PerfilEquipo>('perfiles_equipo'),
          db.getAll<Empresa>('empresa'), db.getAll<Cotizacion>('cotizaciones'),
        ])
        setClientes(c); setPerfiles(p); setEmpresa(e[0] || null)

        if (isEdit) {
          const cot = await db.getById<Cotizacion>('cotizaciones', parseInt(id!))
          if (cot) {
            setNumero(cot.numero); setClienteId(cot.cliente_id); setContacto(cot.cliente_contacto ?? '')
            setTelefono(cot.cliente_telefono ?? ''); setRuc(cot.cliente_ruc ?? ''); setEmail(cot.cliente_email ?? '')
            setProyecto(cot.proyecto_nombre ?? ''); setFecha(cot.fecha_emision ?? ''); setValidez(cot.validez_dias)
            setMoneda(cot.moneda as Moneda); setEstado(cot.estado); setIgvPct(cot.igv_porcentaje); setDescPct(cot.descuento_porcentaje)
            setNotas(cot.notas ?? ''); setSecciones(Array.isArray(cot.secciones) ? cot.secciones : [])
            // Parse equipo items
            const parsed = cot.items as CotizacionItemsEquipo | null
            if (parsed && 'equipo' in parsed) {
              setTeam(parsed.equipo || [])
              setPrecioFinalOverride(parsed.totales?.precio_venta_final ?? null)
            }
          }
        } else {
          setNumero(nextCotNumber(allCot.map(q => q.numero)))
        }
      } catch { showToast('Error al cargar', 'error') }
      finally { setLoading(false) }
    })()
  }, [id])

  function aplicarContacto(ct: ContactoVinculado | null) {
    setContactoSelId(ct?.id ?? null)
    setContacto(ct ? nombreContacto(ct) : '')
    setEmail(ct?.email ?? '')
    setTelefono(ct?.telefono ?? '')
  }

  const fillCliente = useCallback((cId: number) => {
    setClienteId(cId)
    const c = clientes.find(x => x.id === cId)
    if (c) setRuc(c.ruc_dni ?? '')
    const lista = c?.contactos ?? []
    setClienteContactos(lista)
    aplicarContacto(contactoFacturacionDe(c))
  }, [clientes])

  const addMember = () => {
    if (!addPerfilId) { showToast('Selecciona un perfil', 'error'); return }
    const perfil = perfiles.find(p => p.id === addPerfilId)
    if (!perfil) return

    const dedFTE = addDed === 'custom' ? null : parseFloat(addDed)
    const horasCustom = addDed === 'custom' ? addCustomHours : null
    if (addDed === 'custom' && (!horasCustom || horasCustom <= 0)) { showToast('Ingresa las horas', 'error'); return }

    const horasMensuales = dedFTE !== null ? dedFTE * 160 * addCant : (horasCustom ?? 0) * addCant

    const member: EquipoMiembro = {
      perfil_id: perfil.id,
      id_perfil: perfil.id_perfil,
      rol_comercial: perfil.rol_comercial,
      seniority: perfil.seniority,
      cantidad: addCant,
      dedicacion_fte: dedFTE ?? 0,
      horas_custom: horasCustom,
      horas_mensuales_linea: horasMensuales,
      costo_interno_hora: perfil.costo_interno_hora,
      precio_venta_hora: perfil.precio_venta_hora,
      costo_total_linea: r2(horasMensuales * perfil.costo_interno_hora),
      precio_venta_linea: r2(horasMensuales * perfil.precio_venta_hora),
    }

    setTeam(prev => [...prev, member])
    setAddPerfilId(''); setAddCant(1); setAddDed('1.0')
  }

  const save = async () => {
    if (team.length === 0) { showToast('Agrega al menos un integrante', 'error'); return }
    setSaving(true)
    try {
      const clienteNombre = clientes.find(c => c.id === clienteId)?.empresa ?? ''
      const totales: EquipoTotales = {
        total_horas_equipo: totalHoras,
        costo_interno_total: costoTotal,
        precio_venta_sugerido: precioSugerido,
        precio_venta_final: precioFinal,
        margen_global_proyectado: parseFloat(margenGlobal.toFixed(1)),
      }
      const itemsPayload: CotizacionItemsEquipo = { tipo_cotizacion: 'equipo_dedicado', equipo: team, totales }

      const data: Record<string, unknown> = {
        numero, empresa_id: empresa?.id ?? 1, cliente_id: clienteId, cliente_nombre: clienteNombre,
        cliente_contacto: contacto, cliente_telefono: telefono, cliente_ruc: ruc, cliente_email: email,
        fecha_emision: fecha, validez_dias: validez, proyecto_nombre: proyecto,
        items: itemsPayload, subtotal: precioFinal, igv_porcentaje: igvPct, igv_monto: igvMonto,
        descuento_porcentaje: descPct, descuento_monto: descMonto, total, moneda, estado,
        tipo_cotizacion: 'equipo_dedicado',
        metodos_pago: ['Transferencia bancaria - clientes Perú'],
        secciones, notas,
      }
      if (isEdit) { data.id = parseInt(id!); await db.update('cotizaciones', data); showToast('Actualizada', 'success') }
      else { await db.add('cotizaciones', data); showToast('Creada', 'success') }
      nav('/cotizaciones')
    } catch (err) { showToast(err instanceof Error ? err.message : 'Error', 'error') }
    finally { setSaving(false) }
  }

  if (loading) return <div className="page-enter" style={{ padding: '2rem' }}><PageHeader breadcrumbs={[{ label: 'Cotizaciones', to: '/cotizaciones' }, { label: 'Equipo Dedicado' }]} /><div style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 40 }}><div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...</div></div>

  return (
    <div className="page-enter">
      <PageHeader
        breadcrumbs={[{ label: 'Cotizaciones', to: '/cotizaciones' }, { label: `${isEdit ? 'Editar' : 'Nueva'} — Equipo Dedicado` }]}
        meta={`N° ${numero}`}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => nav('/cotizaciones')}>Cancelar</button>
            <button className="btn btn-success" onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
          </>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* LEFT */}
        <div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><h3>Datos del Cliente</h3></div>
            <div className="form-group">
              <label className="form-label">Cliente</label>
              <select className="form-select" value={clienteId ?? ''} onChange={e => fillCliente(parseInt(e.target.value))}>
                <option value="">— Seleccionar cliente —</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
              </select>
            </div>
            {clienteId && (
              <div className="form-group">
                <label className="form-label">Contacto de la empresa</label>
                <SelectorContactoEmpresa contactos={clienteContactos} value={contactoSelId} onSelect={aplicarContacto} />
              </div>
            )}
            <div className="form-row">
              <div className="form-group"><label className="form-label">Contacto</label><input className="form-input" value={contacto} onChange={e => setContacto(e.target.value)} /></div>
              <div className="form-group"><label className="form-label">RUC/DNI</label><input className="form-input" value={ruc} onChange={e => setRuc(e.target.value)} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Teléfono</label><input className="form-input" value={telefono} onChange={e => setTelefono(e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Email</label><input className="form-input" value={email} onChange={e => setEmail(e.target.value)} /></div>
            </div>
          </div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><h3>Proyecto</h3></div>
            <div className="form-group"><label className="form-label">Nombre</label><input className="form-input" value={proyecto} onChange={e => setProyecto(e.target.value)} placeholder="Ej: Célula de Desarrollo — Proyecto Alpha" /></div>
          </div>
          <div className="card">
            <div className="card-header"><h3>Condiciones</h3></div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Fecha</label><input className="form-input" type="date" value={fecha} onChange={e => setFecha(e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Validez (días)</label><input className="form-input" type="number" value={validez} onChange={e => setValidez(parseInt(e.target.value) || 90)} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Moneda</label><select className="form-select" value={moneda} onChange={e => setMoneda(e.target.value as Moneda)}><option value="USD">USD</option><option value="PEN">PEN</option><option value="EUR">EUR</option></select></div>
              <div className="form-group"><label className="form-label">Estado</label><select className="form-select" value={estado} onChange={e => setEstado(e.target.value as EstadoCotizacion)}><option value="borrador">Borrador</option><option value="enviada">Enviada</option><option value="aceptada">Aceptada</option><option value="rechazada">Rechazada</option></select></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">IGV %</label><input className="form-input" type="number" value={igvPct} onChange={e => setIgvPct(parseFloat(e.target.value) || 0)} /></div>
              <div className="form-group"><label className="form-label">Descuento %</label><input className="form-input" type="number" value={descPct} onChange={e => setDescPct(parseFloat(e.target.value) || 0)} /></div>
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div>
          {/* Team Composition */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><h3>Composición del Equipo</h3><span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{team.length} integrante(s)</span></div>
            <div className="eq-add-row" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
              <select className="form-select" style={{ flex: 2 }} value={addPerfilId} onChange={e => setAddPerfilId(parseInt(e.target.value) || '')}>
                <option value="">— Seleccionar perfil —</option>
                {perfiles.map(p => <option key={p.id} value={p.id}>{p.rol_comercial} ({p.seniority}) — ${p.precio_venta_hora.toFixed(0)}/h</option>)}
              </select>
              <input className="form-input" type="number" min="1" value={addCant} onChange={e => setAddCant(parseInt(e.target.value) || 1)} style={{ flex: 0.5 }} placeholder="Cant." />
              <select className="form-select" style={{ flex: 1 }} value={addDed} onChange={e => setAddDed(e.target.value as Dedicacion)}>
                <option value="1.0">Full-Time (160h)</option>
                <option value="0.5">Part-Time (80h)</option>
                <option value="0.25">Quarter (40h)</option>
                <option value="custom">Custom</option>
              </select>
              {addDed === 'custom' && <input className="form-input" type="number" min="1" placeholder="Horas" value={addCustomHours || ''} onChange={e => setAddCustomHours(parseInt(e.target.value) || 0)} style={{ flex: 0.5 }} />}
              <button className="btn btn-primary btn-sm" onClick={addMember}>Agregar</button>
            </div>
            {team.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '12px 0', textAlign: 'center' }}>Agrega integrantes al equipo usando el selector de arriba</p> : (
              <>
                <div className="eq-team-header"><span>Rol</span><span>Seniority</span><span>Cant.</span><span>Dedicación</span><span>Horas/Mes</span><span></span></div>
                {team.map((m, i) => (
                  <div className="eq-team-row" key={i}>
                    <span style={{ fontWeight: 600 }}>{m.rol_comercial}</span>
                    <span><span className={`seniority-badge seniority-${m.seniority.toLowerCase().replace('-', '')}`}>{m.seniority}</span></span>
                    <span>{m.cantidad}</span>
                    <span className={`fte-badge fte-${m.dedicacion_fte === 1.0 ? 'full' : m.dedicacion_fte === 0.5 ? 'half' : 'quarter'}`}>{fmtFTE(m)}</span>
                    <span style={{ fontWeight: 600 }}>{m.horas_mensuales_linea}h</span>
                    <button className="remove-item" onClick={() => setTeam(prev => prev.filter((_, j) => j !== i))}>✕</button>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Internal Dashboard */}
          <div className="card internal-dashboard" style={{ marginBottom: 20 }}>
            <div className="card-header"><h3>Dashboard Interno</h3><span className="badge badge-enviada" style={{ fontSize: '0.7rem' }}>Solo visible para el comercial</span></div>
            {team.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '8px 0' }}>Sin datos aún</p> : (
              <table className="eq-internal-tbl">
                <thead><tr><th>Rol</th><th>Horas</th><th>Costo Int.</th><th>Precio Vta.</th><th>Margen</th></tr></thead>
                <tbody>
                  {team.map((m, i) => {
                    const mg = m.precio_venta_linea > 0 ? ((m.precio_venta_linea - m.costo_total_linea) / m.precio_venta_linea * 100) : 0
                    return <tr key={i}><td>{m.rol_comercial} ({m.seniority}) ×{m.cantidad}</td><td>{m.horas_mensuales_linea}h</td><td style={{ color: 'var(--danger)' }}>${m.costo_total_linea.toFixed(2)}</td><td>${m.precio_venta_linea.toFixed(2)}</td><td><span className={`margin-indicator ${marginClass(mg)}`}>{mg.toFixed(1)}%</span></td></tr>
                  })}
                  <tr className="eq-internal-total"><td><strong>TOTALES</strong></td><td><strong>{totalHoras}h</strong></td><td style={{ color: 'var(--danger)' }}><strong>${costoTotal.toFixed(2)}</strong></td><td><strong>${precioSugerido.toFixed(2)}</strong></td><td><span className={`margin-indicator ${marginClass(margenGlobal)}`} style={{ fontWeight: 700 }}>{margenGlobal.toFixed(1)}%</span></td></tr>
                </tbody>
              </table>
            )}
            {team.length > 0 && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '2px dashed var(--border)' }}>
                <div className="eq-modifier-row">
                  <div><label className="form-label" style={{ marginBottom: 4 }}>Precio Venta Final (Modificador Comercial)</label><p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>Editar para ajustar el precio antes de generar el documento</p></div>
                  <input className="form-input eq-final-price-input" type="number" step="0.01" value={precioFinalOverride ?? precioSugerido} onChange={e => setPrecioFinalOverride(parseFloat(e.target.value) || null)} />
                </div>
                {margenGlobal < MARGEN_MINIMO && (
                  <div className="margin-alert-box" style={{ marginTop: 10, padding: 10, background: 'color-mix(in srgb, var(--danger) 10%, transparent)', borderRadius: 8, fontSize: '0.85rem', color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    Alerta: El margen global ({margenGlobal.toFixed(1)}%) está por debajo del mínimo aceptable ({MARGEN_MINIMO}%)
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Summary Totals */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><h3>Resumen</h3></div>
            <div style={{ paddingTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.9rem' }}><span>Subtotal (Equipo Mensual)</span><strong>{precioFinal.toFixed(2)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.9rem' }}><span>IGV ({igvPct}%)</span><strong>{igvMonto.toFixed(2)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.9rem' }}><span>Descuento</span><strong>-{descMonto.toFixed(2)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '1.1rem', color: 'var(--success)', borderTop: '1px solid var(--border)', marginTop: 4 }}><span style={{ fontWeight: 700 }}>TOTAL MENSUAL</span><strong>{total.toFixed(2)} {moneda}</strong></div>
            </div>
          </div>

          {/* Notes */}
          <div className="card">
            <div className="card-header"><h3>Notas</h3></div>
            <textarea className="form-textarea" value={notas} onChange={e => setNotas(e.target.value)} placeholder="Notas adicionales..." />
          </div>
        </div>
      </div>
    </div>
  )
}
